import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, ArrowRight, LogOut, Save } from "lucide-react";
import { api } from "@/lib/api";
import { TramitadorPicker } from "@/components/TramitadorPicker";
import type { BorradorPaso1Request, MiRegistroLegalResumen, TipoPersona } from "../types";

interface SectorOption {
  id: string;
  codigo: string;
  nombre: string;
}

type ModoRegistro = "TITULAR" | "TRAMITADOR" | "TITULAR_CON_TRAMITADOR";

interface TramitadorLite {
  id: string;
  razonSocial: string;
  rfc: string;
  estatus: string;
}

/**
 * Paso 1: datos generales de la empresa. Auto-save al avanzar; el backend
 * valida RFC con regex permisivo (3-4 letras); la validación estricta por
 * tipo de persona ocurre en `enviar-revision`.
 */
export function Paso1Datos({
  rl,
  onSaved,
  onSalir,
}: {
  rl: MiRegistroLegalResumen;
  onSaved: () => void;
  onSalir: () => void;
}) {
  const qc = useQueryClient();
  // Si los placeholders del borrador inicial siguen, los limpiamos del UI.
  const limpio = (s: string | null | undefined, placeholder: string) =>
    s && s !== placeholder ? s : "";

  // Derivar el modo inicial a partir de las banderas + tramitadorId persistidos.
  const modoInicial: ModoRegistro =
    rl.tramitadorId
      ? "TITULAR_CON_TRAMITADOR"
      : rl.puedeActuarComoTramitador && !rl.puedeActuarComoTitular
        ? "TRAMITADOR"
        : "TITULAR";
  const [modo, setModo] = useState<ModoRegistro>(modoInicial);
  const [tramitador, setTramitador] = useState<TramitadorLite | null>(
    rl.tramitadorId && rl.tramitadorRazonSocial
      ? { id: rl.tramitadorId, razonSocial: rl.tramitadorRazonSocial, rfc: "", estatus: "" }
      : null,
  );

  const [form, setForm] = useState<BorradorPaso1Request>({
    tipoPersona: rl.tipoPersona,
    puedeActuarComoTitular: rl.puedeActuarComoTitular,
    puedeActuarComoTramitador: rl.puedeActuarComoTramitador,
    razonSocial: limpio(rl.razonSocial, "(Borrador en captura)"),
    nombreComercial: rl.nombreComercial ?? "",
    rfc: rl.rfc.startsWith("BORRADOR-") ? "" : rl.rfc,
    curp: rl.curp ?? "",
    giroEmpresa: rl.giroEmpresa ?? "",
    correoPrincipal: rl.correoPrincipal === "borrador@pendiente.local" ? "" : rl.correoPrincipal,
    telefono: limpio(rl.telefono, "0000000000"),
    paginaElectronica: rl.paginaElectronica ?? "",
    sectoresAutorizados: rl.sectoresAutorizados ?? [],
    tramitadorId: rl.tramitadorId ?? null,
  });
  const [error, setError] = useState<string | null>(null);

  // Sincroniza banderas + tramitadorId del form cuando cambia el modo.
  const cambiarModo = (m: ModoRegistro) => {
    setModo(m);
    if (m === "TITULAR") {
      setForm((f) => ({ ...f, puedeActuarComoTitular: true, puedeActuarComoTramitador: false, tramitadorId: null }));
      setTramitador(null);
    } else if (m === "TRAMITADOR") {
      setForm((f) => ({ ...f, puedeActuarComoTitular: false, puedeActuarComoTramitador: true, tramitadorId: null }));
      setTramitador(null);
    } else {
      // TITULAR_CON_TRAMITADOR
      setForm((f) => ({ ...f, puedeActuarComoTitular: true, puedeActuarComoTramitador: false }));
    }
  };

  const qSectores = useQuery<SectorOption[]>({
    queryKey: ["sectores"],
    queryFn: async () => (await api.get<SectorOption[]>("/api/catalogos/sectores")).data,
    staleTime: 5 * 60_000,
  });

  const guardar = useMutation({
    mutationFn: async (payload: BorradorPaso1Request) => {
      await api.patch(`/api/mi-registro-legal/borrador/${rl.id}/paso/1`, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mi-registro-legal"] });
      onSaved();
    },
    onError: (e: unknown) => {
      const r = e as { response?: { data?: { detail?: string; title?: string } } };
      setError(r.response?.data?.detail || r.response?.data?.title || (e as Error).message);
    },
  });

  const toggleSector = (id: string) =>
    setForm((f) => ({
      ...f,
      sectoresAutorizados: (f.sectoresAutorizados ?? []).includes(id)
        ? (f.sectoresAutorizados ?? []).filter((x) => x !== id)
        : [...(f.sectoresAutorizados ?? []), id],
    }));

  const validarLocal = (): string | null => {
    if (!form.puedeActuarComoTitular && !form.puedeActuarComoTramitador) {
      return "Debes marcar al menos una bandera: titular o tramitador.";
    }
    if (modo === "TITULAR_CON_TRAMITADOR" && !tramitador) {
      return "Selecciona el tramitador externo asociado.";
    }
    if (form.tipoPersona === "FISICA" && !(form.curp ?? "").trim()) {
      return "Persona física requiere CURP.";
    }
    return null;
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const err = validarLocal();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    const payload: BorradorPaso1Request = {
      ...form,
      tramitadorId: modo === "TITULAR_CON_TRAMITADOR" ? tramitador?.id ?? null : null,
    };
    setForm(payload);
    guardar.mutate(payload);
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <Section title="Tipo de persona y rol">
        <Field label="¿Es persona física o moral?" full>
          <div className="flex gap-2">
            {(["MORAL", "FISICA"] as TipoPersona[]).map((tp) => (
              <button
                type="button"
                key={tp}
                onClick={() => setForm({ ...form, tipoPersona: tp })}
                className={`rounded border px-4 py-2 text-sm ${
                  form.tipoPersona === tp
                    ? "border-primary bg-primary text-white"
                    : "border-edge-strong bg-surface text-fg-muted hover:bg-surface-2"
                }`}
              >
                {tp === "MORAL" ? "Moral" : "Física"}
              </button>
            ))}
          </div>
        </Field>
        <Field label="¿Cómo actuará tu empresa?" full>
          <div className="space-y-2">
            {[
              {
                id: "TITULAR" as const,
                label: "Titular",
                hint: "Mi empresa solicita servicios directamente, sin tramitador externo.",
              },
              {
                id: "TRAMITADOR" as const,
                label: "Tramitador externo",
                hint: "Gestiono trámites en representación de terceros.",
              },
              {
                id: "TITULAR_CON_TRAMITADOR" as const,
                label: "Empresa Titular con Tramitador",
                hint: "Empresa titular cuyos trámites los opera un tramitador externo ya registrado.",
              },
            ].map((opt) => (
              <label
                key={opt.id}
                className={`flex cursor-pointer items-start gap-2 rounded border p-3 text-sm transition ${
                  modo === opt.id
                    ? "border-primary-strong bg-primary-tint"
                    : "border-edge hover:border-edge-strong"
                }`}
              >
                <input
                  type="radio"
                  name="modo-registro"
                  checked={modo === opt.id}
                  onChange={() => cambiarModo(opt.id)}
                  className="mt-0.5"
                />
                <div>
                  <div className="font-medium text-fg">{opt.label}</div>
                  <div className="text-xs text-fg-muted">{opt.hint}</div>
                </div>
              </label>
            ))}
          </div>
        </Field>
        {modo === "TITULAR_CON_TRAMITADOR" && (
          <Field label="Tramitador externo *" full>
            <TramitadorPicker value={tramitador} onChange={(t) => setTramitador(t)} />
            <p className="mt-1 text-xs text-fg-subtle">
              Solo aparecen registros legales que ya tienen la bandera Tramitador. Si el tramitador
              aún no existe, pídele a Calidad que lo dé de alta primero.
            </p>
          </Field>
        )}
      </Section>

      <Section title="Identidad">
        <Field label="Razón social *" full>
          <input
            type="text"
            required
            maxLength={255}
            value={form.razonSocial}
            onChange={(e) => setForm({ ...form, razonSocial: e.target.value })}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          />
        </Field>
        <Field label="Nombre comercial">
          <input
            type="text"
            maxLength={255}
            value={form.nombreComercial ?? ""}
            onChange={(e) => setForm({ ...form, nombreComercial: e.target.value })}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          />
        </Field>
        <Field label="RFC *">
          <input
            type="text"
            required
            maxLength={13}
            value={form.rfc}
            onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })}
            className="w-full rounded border px-2 py-1.5 font-mono text-sm uppercase"
          />
        </Field>
        {form.tipoPersona === "FISICA" && (
          <Field label="CURP * (requerido para persona física)">
            <input
              type="text"
              maxLength={18}
              required
              value={form.curp ?? ""}
              onChange={(e) => setForm({ ...form, curp: e.target.value.toUpperCase() })}
              className="w-full rounded border px-2 py-1.5 font-mono text-sm uppercase"
            />
          </Field>
        )}
        <Field label="Giro de la empresa">
          <input
            type="text"
            maxLength={255}
            value={form.giroEmpresa ?? ""}
            onChange={(e) => setForm({ ...form, giroEmpresa: e.target.value })}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          />
        </Field>
      </Section>

      <Section title="Contacto">
        <Field label="Correo principal *">
          <input
            type="email"
            required
            value={form.correoPrincipal}
            onChange={(e) => setForm({ ...form, correoPrincipal: e.target.value })}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          />
        </Field>
        <Field label="Teléfono *">
          <input
            type="text"
            required
            value={form.telefono}
            onChange={(e) => setForm({ ...form, telefono: e.target.value })}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          />
        </Field>
        <Field label="Página electrónica">
          <input
            type="text"
            value={form.paginaElectronica ?? ""}
            onChange={(e) => setForm({ ...form, paginaElectronica: e.target.value })}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          />
        </Field>
      </Section>

      <Section title="Sectores de tu interés">
        <Field label="Selecciona uno o más (opcional ahora, obligatorio para enviar)" full>
          <div className="flex flex-wrap gap-2">
            {(qSectores.data ?? []).map((s) => {
              const active = (form.sectoresAutorizados ?? []).includes(s.id);
              return (
                <button
                  type="button"
                  key={s.id}
                  onClick={() => toggleSector(s.id)}
                  className={`rounded border px-3 py-1.5 text-sm ${
                    active
                      ? "border-primary bg-primary text-white"
                      : "border-edge-strong bg-surface text-fg-muted hover:bg-surface-2"
                  }`}
                >
                  {s.nombre}
                </button>
              );
            })}
          </div>
        </Field>
      </Section>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border bg-estado-warn-bg p-3 text-sm text-estado-warn">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <BotonesPaso
        loading={guardar.isPending}
        labelSiguiente="Guardar y continuar"
        onSalir={onSalir}
      />
    </form>
  );
}

// ---- componentes auxiliares compartidos (también usados por otros pasos) ---

export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border bg-surface shadow-card">
      <h2 className="border-b px-4 py-2 text-xs font-semibold uppercase tracking-wide text-fg-subtle">{title}</h2>
      <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
    </section>
  );
}

export function Field({ label, children, full }: { label: string; children: React.ReactNode; full?: boolean }) {
  return (
    <div className={full ? "sm:col-span-2 lg:col-span-3" : ""}>
      <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-fg-subtle">{label}</div>
      {children}
    </div>
  );
}

export function BotonesPaso({
  loading,
  labelSiguiente,
  onSalir,
  onAtras,
  submit = true,
}: {
  loading: boolean;
  labelSiguiente: string;
  onSalir: () => void;
  onAtras?: () => void;
  submit?: boolean;
}) {
  return (
    <div className="flex flex-wrap justify-end gap-2 border-t bg-surface-2 px-4 py-3">
      <button
        type="button"
        onClick={onSalir}
        disabled={loading}
        className="inline-flex items-center gap-1 rounded border px-3 py-1.5 text-sm text-fg-muted hover:bg-surface-2 disabled:opacity-50"
      >
        <LogOut className="h-4 w-4" />
        Guardar y salir
      </button>
      {onAtras && (
        <button
          type="button"
          onClick={onAtras}
          disabled={loading}
          className="rounded border px-3 py-1.5 text-sm text-fg-muted hover:bg-surface-2 disabled:opacity-50"
        >
          ← Atrás
        </button>
      )}
      <button
        type={submit ? "submit" : "button"}
        disabled={loading}
        className="inline-flex items-center gap-1 rounded bg-primary px-4 py-1.5 text-sm font-medium text-white hover:bg-primary-hover disabled:opacity-50"
      >
        {loading ? <Save className="h-4 w-4 animate-pulse" /> : <ArrowRight className="h-4 w-4" />}
        {loading ? "Guardando…" : labelSiguiente}
      </button>
    </div>
  );
}
